import { Loading, Pullout, PulloutType } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import HtmlParser from 'html-react-parser';
import _ from 'lodash';
import styled from 'styled-components';
import ContentConstants from '../../contentConstants';
import {
	AudienceSourceType,
	DistributionType,
	IBreakoutModel,
	IBreakoutModelVm,
	ISurveyBuilderActionCreators,
	SurveyStep,
} from '../../models';
import { ISurveyBuilderState } from '../../store/SurveyBuilderStore';
import { SurveyHelper } from '../../utilities';
import { DistributionHelper } from '../../utilities/DistributionHelper';
import { ClearDiv, Col3Col9SelectWithCrumb, LoadingWrapper, Section } from '../common';
import { BodyCopyLink } from '../common/styles/globalStyles';

export const UnavailableContainer = styled.div`
	text-align: center;
	position: relative;
`;
export const UnavailableTitleContainer = styled.div`
	font-weight: 700;
	font-size: 16px;
	margin-bottom: 40px;
`;
export const UnavailableText3Container = styled.div`
	margin-top: 20px;
`;

export type IParticipationMonitorBreakoutProps = ISurveyBuilderActionCreators &
	ISurveyBuilderState & {
		availableBreakouts: IBreakoutModelVm;
		selectedBreakouts: IBreakoutModel;
		missingBreakouts: IBreakoutModel;
		audienceSourceType?: AudienceSourceType;
		audienceId: number;
		distributionType?: DistributionType;
		hrisId?: string;
		hrisExists: boolean;
		hrisFileName: string;
		loading: boolean;
		onSelectedAttributesChange: (attributes: string[]) => void;
		onSelectedHierarchiesChange: (attributes: string[]) => void;
	};

export class ParticipationMonitorBreakouts extends React.PureComponent<IParticipationMonitorBreakoutProps> {
	public constructor(props: IParticipationMonitorBreakoutProps) {
		super(props);
		this.jumpTo = this.jumpTo.bind(this);
	}

	public render() {
		const { loading } = this.props;

		return (
			<Section
				id={ContentConstants.ID_PARTICIPATION_MONITOR_ATTRIBUTES}
				sectionHeader={ContentConstants.SECTIONHEADERS_PARTICIPATION_MONITOR_ATTRIBUTE}
			>
				{loading ? (
					<LoadingWrapper>
						<Loading height={'70px'} width={'70px'} />
					</LoadingWrapper>
				) : (
					this.renderBreakouts()
				)}
			</Section>
		);
	}

	private renderBreakouts() {
		// === If source was not configured === //
		const { audienceSourceType: sourceType, hrisId } = this.props;

		if (!sourceType || (sourceType === AudienceSourceType.Hris && !hrisId)) {
			return (
				<UnavailableContainer>
					<UnavailableTitleContainer>
						{ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_DATASOURCE_TITLE}
					</UnavailableTitleContainer>

					<span>{HtmlParser(ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_DATASOURCE)}</span>

					<BodyCopyLink onClick={() => this.jumpTo(SurveyStep.Basics)}>
						{ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_DATASOURCE_LINK}
					</BodyCopyLink>

					<span>{ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_DATASOURCE_2}</span>

					<UnavailableText3Container>
						{ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_DATASOURCE_3}
					</UnavailableText3Container>
				</UnavailableContainer>
			);
		}

		// === If configured with an unsupported source === //
		if (sourceType === AudienceSourceType.Hierarchy) {
			return (
				<UnavailableContainer>
					<UnavailableTitleContainer>
						{ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_DATASOURCE_TITLE}
					</UnavailableTitleContainer>

					<span>{HtmlParser(ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_DISTRIBUTION_HIERARCHY)}</span>
				</UnavailableContainer>
			);
		}

		// === If distribution configuration is invalid for section, or no breakout selections are available for source === //
		const { distributionType, availableBreakouts, hrisExists } = this.props;

		if (
			DistributionHelper.isAnonymous(distributionType) ||
			(sourceType === AudienceSourceType.FileUpload && availableBreakouts.attributes.length === 0)
		) {
			return (
				<UnavailableContainer>
					<UnavailableTitleContainer>
						{ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_DATASOURCE_TITLE}
					</UnavailableTitleContainer>

					<span>{ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_DISTRIBUTION}</span>

					<BodyCopyLink onClick={() => this.jumpTo(SurveyStep.Distribution)}>
						{ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_DISTRIBUTION_LINK}
					</BodyCopyLink>

					<span>{HtmlParser(ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_DISTRIBUTION_2)}</span>

					<UnavailableText3Container>
						{ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_DATASOURCE_3}
					</UnavailableText3Container>
				</UnavailableContainer>
			);
		}

		if (sourceType === AudienceSourceType.Hris && hrisExists && availableBreakouts.hierarchies.length === 0) {
			return (
				<UnavailableContainer>
					<UnavailableTitleContainer>
						{ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_HIERARCHIES_TITLE}
					</UnavailableTitleContainer>

					<span>{ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_HIERARCHIES}</span>

					<BodyCopyLink onClick={() => this.jumpTo(SurveyStep.Basics)}>
						{ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_DATASOURCE_LINK}
					</BodyCopyLink>

					<span>{ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_NO_HIERARCHIES_2}</span>
				</UnavailableContainer>
			);
		}

		// ================================================================================= //

		const { missingBreakouts } = this.props;
		const allMissingBreakouts = _.concat(missingBreakouts.attributes, missingBreakouts.hierarchies);

		return (
			<div>
				{allMissingBreakouts.length > 0 && (
					<div>
						<Pullout type={PulloutType.Error}>
							<strong>DATA MISSING:</strong> The data ({_.values(allMissingBreakouts).join(',')}) no longer exist and were removed as
							selections.
							<strong> Please save again to confirm the selections below.</strong>
						</Pullout>
						<ClearDiv />
					</div>
				)}
				{sourceType === AudienceSourceType.Hris && !hrisExists && (
					<>
						<Pullout type={PulloutType.Warning}>{HtmlParser(ContentConstants.EMPLOYEEDATASOURCE_NOTAVAIL_WARNING)}</Pullout>
						<ClearDiv />
					</>
				)}

				{this.renderAttributeBreakoutSelector()}
				<ClearDiv />
				{sourceType == AudienceSourceType.Hris && this.renderHierarchyBreakoutSelector()}
			</div>
		);
	}

	private renderAttributeBreakoutSelector() {
		const { availableBreakouts, selectedBreakouts } = this.props;
		const { onSelectedAttributesChange } = this.props;

		const selectedAttributeCrumbs = SurveyHelper.getCrumbsFromAttributes(
			selectedBreakouts.attributes,
			availableBreakouts.attributes,
			false,
		);
		const availableAttributeItems = SurveyHelper.getSelectOptionsFromAttributes(availableBreakouts.attributes);

		return (
			<Col3Col9SelectWithCrumb
				label={ContentConstants.SUBSECTIONHEADERS_PARTICIPATION_MONITOR_ATTRIBUTE}
				instructions={ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_ATTRIBUTES}
				placeHolderText={ContentConstants.INPUT_PLACEHOLDER_SELECT_ATTRIBUTE}
				availableItems={availableAttributeItems}
				selectedItems={this.getSelectedAttributes(selectedBreakouts.attributes, availableBreakouts.attributes)}
				crumbs={selectedAttributeCrumbs}
				onSelectedChange={onSelectedAttributesChange}
			/>
		);
	}

	private renderHierarchyBreakoutSelector() {
		const { availableBreakouts, selectedBreakouts } = this.props;
		const { onSelectedHierarchiesChange } = this.props;

		const selectedHierarchyCrumbs = SurveyHelper.getCrumbsFromHierarchyAttributes(
			selectedBreakouts.hierarchies,
			availableBreakouts.hierarchies,
			false,
		);
		const availableAttributeHierarchyItems = SurveyHelper.getSelectOptionsFromHierarchyAttributes(availableBreakouts.hierarchies);

		return (
			<Col3Col9SelectWithCrumb
				label={ContentConstants.SUBSECTIONHEADERS_PARTICIPATION_MONITOR_HIERARCHY}
				instructions={ContentConstants.INSTRUCTIONALTEXT_REPORTING_PARTICIPATION_MONITOR_HIERARCHIES}
				placeHolderText={ContentConstants.INPUT_PLACEHOLDER_SELECT_HIERARCHY}
				availableItems={availableAttributeHierarchyItems}
				selectedItems={selectedBreakouts.hierarchies}
				crumbs={selectedHierarchyCrumbs}
				onSelectedChange={onSelectedHierarchiesChange}
			/>
		);
	}

	private jumpTo(surveyStep: SurveyStep) {
		const { surveyId } = this.props;
		if (surveyId !== undefined && surveyId !== 0) {
			this.props.Jump(surveyStep);
		}
	}

	// The value we store in our db and the value coming in from the external system may have different casings
	// The select component does not do a case insensitive matching on selectedItems.
	// This re-creates the list by doing the case insensitive matching here where we have access to both collections.
	private getSelectedAttributes(internal: string[], external: string[]) {
		const selected: string[] = [];

		_.forEach(internal, (attr: string) => {
			const ext = _.find(external, (e: string) => e.toUpperCase() === attr.toUpperCase());
			selected.push(ext || attr);
		});

		return selected;
	}
}
